import { useSetRecoilState } from 'recoil';
import { workspaceDisplayArrangementState } from './state';
import { getWorkspaceLayoutSnapshot } from './getWorkspaceLayoutSnapshot';
import { useEffect } from 'react';
import { getIndexedDisplays, buildDisplaySlots } from './utils';
import { logger } from 'modules/logger';
import {
  addExtensionListener,
  removeExtensionListener,
} from 'domains/extension/extensionEventCreators';
import { RESPONSE_EVENTS } from 'domains/extension/constants';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

export function useWorkspaceDisplayArrangement() {
  const [isWorkspaceLayoutV2Enabled] = useFeatureFlagEnabled(FF.WORKSPACE_LAYOUT_V2);
  const setWorkspaceDisplayArrangement = useSetRecoilState(workspaceDisplayArrangementState);

  useEffect(() => {
    // Only layouts use the display arrangement, so V1 users never ask the extension for it
    if (!isWorkspaceLayoutV2Enabled) return;

    const capture = async (trigger: 'page-load' | 'display-change') => {
      try {
        const snapshot = await getWorkspaceLayoutSnapshot();
        const displays = getIndexedDisplays(snapshot.displays);
        logger.info('[WorkspaceLayoutManager] Captured display arrangement', {
          trigger,
          displays,
        });
        setWorkspaceDisplayArrangement({ displays: buildDisplaySlots(displays) });
      } catch (error) {
        logger.warn('[WorkspaceLayoutManager] Failed to capture display arrangement', error);
      }
    };
    void capture('page-load');

    const onDisplayChanged = () => void capture('display-change');
    addExtensionListener(RESPONSE_EVENTS.DISPLAY_CHANGED, onDisplayChanged);
    return () => removeExtensionListener(RESPONSE_EVENTS.DISPLAY_CHANGED, onDisplayChanged);
  }, [isWorkspaceLayoutV2Enabled, setWorkspaceDisplayArrangement]);
}
